import React, { useEffect, useMemo, useRef } from 'react';
import * as d3 from 'd3';

/**
 * Horizontal bar chart that visualizes top publishing departments by count.
 * Props:
 *  - data: Array<{ id, name, count }>
 *  - maxItems: number (default 5)
 *  - height: number (optional). If not provided, height is computed from rows.
 *  - barColor: string (default '#4F55FF')
 */
export function MajorPublishingDepartments({ data = [], maxItems = 5, height, barColor = '#4F55FF' }) {
  const ref = useRef(null);
  const tooltipRef = useRef(null);

  const rows = useMemo(() => {
    const arr = Array.isArray(data) ? data.filter(Boolean) : [];
    return [...arr]
      .filter(d => typeof d?.count === 'number' && d.count > 0 && d?.name)
      .sort((a, b) => (b.count || 0) - (a.count || 0))
      .slice(0, maxItems);
  }, [data, maxItems]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerHTML = '';

    if (!rows.length) return;

    const width = el.clientWidth || 600;
    const rowH = 45; // bar thickness + gap
    const innerRows = rows.length;
    const heightPx = height || Math.max(120, 20 + innerRows * rowH + 30);

    const margin = { top: 6, right: 16, bottom: 30, left: 140 }; // left margin을 100에서 140으로 늘림

    const svg = d3.select(el)
      .append('svg')
      .attr('width', '100%')
      .attr('height', heightPx)
      .attr('viewBox', `0 0 ${width} ${heightPx}`);

    const innerW = width - margin.left - margin.right;
    const innerH = heightPx - margin.top - margin.bottom;

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    const y = d3.scaleBand()
      .domain(rows.map(d => d.name))
      .range([0, innerH])
      .padding(0.25);

    const maxX = d3.max(rows, d => d.count) || 0;
    const x = d3.scaleLinear()
      .domain([0, maxX * 1.1])
      .range([0, innerW])
      .nice();

    // gridlines (x)
    // x축 + gridlines (아래쪽)
    g.append('g')
      .attr('transform', `translate(0,${innerH})`)   // ⬅️ 아래로 내림
      .call(
        d3.axisBottom(x)                             // ⬅️ axisTop → axisBottom
          .ticks(6)                                  // 적당한 tick 개수
          .tickSize(-innerH)                         // 위로 올라가는 grid line
          .tickFormat(d => `${d}`)
      )
      .call(g => {
        g.selectAll('.domain').remove();
        g.selectAll('line').attr('stroke', '#E4E8F1');
        g.selectAll('text')
          .attr('fill', '#8D9AAF')
          .style('font-size', 12);
      });

    // y-axis labels
    g.append('g')
      .call(d3.axisLeft(y).tickSize(0))
      .call(g => {
        g.selectAll('.domain').remove();
        g.selectAll('text')
          .attr('fill', '#6C7687')
          .style('font-size', 15) // 폰트 크기를 11에서 13으로 키움
          .each(function (d) {
            const text = d3.select(this);
            const words = d.split('');
            if (d.length > 8) {
              const mid = Math.ceil(d.length / 2);
              const line1 = d.substring(0, mid);
              const line2 = d.substring(mid);
              text.text(null);
              text.append('tspan').attr('x', -8).attr('dy', '-0.3em').text(line1); // x 위치 조정
              text.append('tspan').attr('x', -8).attr('dy', '1.1em').text(line2); // x 위치 조정
            }
          });
      });

    // bars
    g.selectAll('.bar')
      .data(rows)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('y', d => y(d.name) || 0)
      .attr('x', 0)
      .attr('height', y.bandwidth())
      .attr('rx', 3)
      .attr('width', 0)
      .attr('fill', barColor)
      .attr('opacity', 0.95)
      .transition()
      .duration(600)
      .attr('width', d => x(d.count));

    // value labels at end of bars
    // g.selectAll('.val')
    //   .data(rows)
    //   .enter()
    //   .append('text')
    //   .attr('class', 'val')
    //   .attr('x', d => x(d.count) + 6)
    //   .attr('y', d => (y(d.name) || 0) + y.bandwidth() / 2 + 4)
    //   .attr('fill', '#2A2E36')
    //   .style('font-size', 12)
    //   .text(d => d.count);

    // tooltips
    const tooltip = d3.select(tooltipRef.current);
    g.selectAll('.hit')
      .data(rows)
      .enter()
      .append('rect')
      .attr('class', 'hit')
      .attr('y', d => y(d.name) || 0)
      .attr('x', 0)
      .attr('width', innerW)
      .attr('height', y.bandwidth())
      .attr('fill', 'transparent')
      .on('mousemove', function (event, d) {
        const [mx, my] = d3.pointer(event, this);
        tooltip
          .style('display', 'block')
          .style('left', `${margin.left + mx + 10}px`)
          .style('top', `${margin.top + my - 20}px`)
          .html(`<div class='px-2 py-1 text-[12px]'><span class='font-semibold mr-2' style='color:${barColor}'>${d.count}</span><span class='text-[#2A2E36]'>${d.name}</span></div>`);
      })
      .on('mouseleave', () => tooltip.style('display', 'none'));
  }, [rows, height, barColor]);

  if (!rows.length) {
    return <div className="text-[14px] text-[#6C7687]">No department data.</div>;
  }

  return (
    <div className="relative">
      <div ref={ref} className="w-full" />
      <div ref={tooltipRef} className="absolute z-10 border border-[#E4E8F1] bg-white rounded shadow-sm" style={{ display: 'none' }} />
    </div>
  );
}

export default MajorPublishingDepartments;
